import { useState, useRef, useEffect } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Button from '@cloudscape-design/components/button'
import Badge from '@cloudscape-design/components/badge'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Textarea from '@cloudscape-design/components/textarea'
import Flashbar from '@cloudscape-design/components/flashbar'
import ProgressBar from '@cloudscape-design/components/progress-bar'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import {
  useEnrichProviderEmail,
  useGenerateOutreachEmail,
  useOutreachRun,
  useStreamOutreachEmail,
  useCheckCompliance,
  usePublishOutreachEmail,
} from '@/hooks/useProviders'
import { useOutreachStore } from '@/state/outreachStore'
import { useQuery } from '@apollo/client/react'
import { GET_OUTREACH_RUN } from '@/lib/operations/providers'
import Select from '@cloudscape-design/components/select'
import type { SelectProps } from '@cloudscape-design/components/select'
import type { Provider, FitScore, EmailStyleOverride, FrameworkOverride, PsychologyStrategy } from '@/lib/types'
import { humanizeNodeName } from '@/utils/format'

const EMAIL_STYLE_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'Cold', value: 'cold' },
  { label: 'Consultative', value: 'consultative' },
]

const FRAMEWORK_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'TIA', value: 'TIA' },
  { label: 'OPPA', value: 'OPPA' },
  { label: 'QVA', value: 'QVA' },
]

const PSYCHOLOGY_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'Loss Aversion', value: 'loss_aversion' },
  { label: 'Expertise', value: 'expertise' },
  { label: 'Value First', value: 'value_first' },
  { label: 'Partnership', value: 'partnership' },
]

interface GenerateEmailModalProps {
  provider: Provider
  clientId: string
  onDismiss: () => void
}

function computeFitScore(p: Provider): FitScore {
  let score = 0
  const talking_points: string[] = []
  if (p.prescribing_volume_annual_usd !== null) {
    if (p.prescribing_volume_annual_usd > 100_000) {
      score += 4
      talking_points.push(`$${(p.prescribing_volume_annual_usd / 1000).toFixed(0)}k annual Medicare volume`)
    } else if (p.prescribing_volume_annual_usd > 50_000) {
      score += 2
      talking_points.push(`$${(p.prescribing_volume_annual_usd / 1000).toFixed(0)}k annual Medicare volume`)
    }
  }
  if (p.engagement_signals.length > 0) {
    score += 2
    talking_points.push(`${p.engagement_signals.length} prior engagement signal(s)`)
  }
  if (p.cms_rank_by_volume !== null && p.cms_rank_by_volume <= 100) {
    score += 1
    talking_points.push(`Ranked #${p.cms_rank_by_volume} by Medicare volume`)
  }
  const total = Math.min(score, 10)
  return {
    total,
    label: total >= 7 ? 'Strong Match' : total >= 4 ? 'Good Fit' : 'Low Priority',
    color: total >= 7 ? 'emerald' : total >= 4 ? 'amber' : 'zinc',
    talking_points,
  }
}

export default function GenerateEmailModal({ provider, clientId, onDismiss }: GenerateEmailModalProps) {
  const [runId, setRunId] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [email, setEmail] = useState(provider.email ?? '')
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [queued, setQueued] = useState(false)
  const [emailStyleOverride, setEmailStyleOverride] = useState<EmailStyleOverride>('automatic')
  const [frameworkOverride, setFrameworkOverride] = useState<FrameworkOverride>('automatic')
  const [psychologyStrategy, setPsychologyStrategy] = useState<PsychologyStrategy>('automatic')
  const [settingsExpanded, setSettingsExpanded] = useState(false)

  const { enrichEmail } = useEnrichProviderEmail()
  const { generateEmail, loading: generating } = useGenerateOutreachEmail()
  const { stream, isStreaming } = useStreamOutreachEmail()
  const { run, startPolling, stopPolling } = useOutreachRun(clientId, runId)
  const { checkCompliance, result: compliance } = useCheckCompliance()
  const { publishEmail, loading: publishing } = usePublishOutreachEmail()

  const agentStatus = useOutreachStore((s) => s.agentStatus)
  const nodeStatuses = useOutreachStore((s) => s.nodeStatuses)
  const outreachEmail = useOutreachStore((s) => s.email)
  const errorMessage = useOutreachStore((s) => s.errorMessage)
  const resetStore = useOutreachStore((s) => s.reset)
  const outreachProvenance = useOutreachStore((s) => s.provenance)

  const fit = computeFitScore(provider)

  function mapBadgeColor(color: string): 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral' | undefined {
    switch (color) {
      case 'emerald':
        return 'green'
      case 'amber':
        return 'blue'
      case 'zinc':
        return 'grey'
      default:
        return undefined
    }
  }

  // One-shot query to seed the ordered node list — no polling
  const { data: runData } = useQuery<{
    outreachRun: { graphNodes: string[]; completedNodes: string[]; progressPct: number }
  }>(GET_OUTREACH_RUN, {
    variables: { clientId, runId: runId ?? '' },
    skip: !clientId || !runId,
    fetchPolicy: 'network-only',
  })

  // Use graphNodes from query if available, otherwise derive from nodeStatuses
  const graphNodesFromQuery = runData?.outreachRun?.graphNodes ?? []
  const graphNodesFromSSE = Object.keys(nodeStatuses)
  const graphNodes = graphNodesFromQuery.length > 0 ? graphNodesFromQuery : graphNodesFromSSE

  const enrichedRef = useRef(false)

  // Email enrichment — use a useRef guard (NOT useEffect for fetching)
  if (!enrichedRef.current && !provider.email) {
    enrichedRef.current = true
    enrichEmail(clientId, provider._id)
      .then((res) => {
        const enriched = res.data?.enrichProviderEmail?.email
        if (enriched) setEmail(enriched)
      })
      .catch(() => {})
  }

  // Polling fallback — only activate when SSE never connected
  useEffect(() => {
    if (agentStatus === 'running' && !isStreaming) {
      startPolling(2000)
    } else {
      stopPolling()
    }
  }, [agentStatus, isStreaming, startPolling, stopPolling])

  // Sync completion from polling fallback (run.status)
  useEffect(() => {
    if (!run?.status) return
    if (run.status === 'complete' && run.email && agentStatus !== 'complete') {
      setSubject(run.email.subject)
      setBody(run.email.body)
      setRunId(null)
      checkCompliance(clientId, provider._id, run.email.subject, run.email.body)
      if (run.provenance?.length) {
        useOutreachStore.getState().setProvenance(run.provenance)
      }
    }
    if (run.status === 'error' && agentStatus !== 'error') {
      setRunId(null)
      setFlash([
        {
          type: 'error',
          content: run.error
            ? `Email generation failed: ${run.error}`
            : 'Email generation failed. Please try again.',
          dismissible: true,
          onDismiss: () => setFlash([]),
        },
      ])
    }
  }, [run?.status, agentStatus, clientId, provider._id, checkCompliance])

  // Cleanup on unmount — reset store so re-opening the modal starts fresh
  useEffect(() => {
    return () => { resetStore() }
  }, [resetStore])

  // Populate form fields when SSE stream completes
  useEffect(() => {
    if (outreachEmail && agentStatus === 'complete') {
      setSubject(outreachEmail.subject)
      setBody(outreachEmail.body)
      setRunId(null)
      checkCompliance(clientId, provider._id, outreachEmail.subject, outreachEmail.body)
    }
  }, [outreachEmail, agentStatus])

  // Show error flash from SSE
  useEffect(() => {
    if (agentStatus === 'error' && errorMessage) {
      setRunId(null)
      setFlash([
        {
          type: 'error',
          content: `Email generation failed: ${errorMessage}`,
          dismissible: true,
          onDismiss: () => setFlash([]),
        },
      ])
    } else if (agentStatus === 'error' && !errorMessage) {
      setRunId(null)
      setFlash([
        {
          type: 'error',
          content: 'Email generation failed. Please try again.',
          dismissible: true,
          onDismiss: () => setFlash([]),
        },
      ])
    }
  }, [agentStatus, errorMessage])

  async function handleGenerate() {
    setSettingsExpanded(false)
    setFlash([])
    setRunId(null)
    resetStore()
    try {
      const res = await generateEmail(clientId, provider._id)
      const id = res.data?.generateOutreachEmail?.runId
      if (id) {
        setRunId(id)
        void stream(clientId, id, provider._id, {
          emailStyleOverride,
          frameworkOverride,
          psychologyStrategy,
        })
      } else {
        setFlash([{ type: 'error', content: 'No run ID returned. Please try again.', dismissible: true, onDismiss: () => setFlash([]) }])
      }
    } catch (err) {
      setFlash([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to start generation.',
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
    }
  }

  async function handleAddToQueue() {
    try {
      await publishEmail(clientId, provider._id, subject, body, email || undefined)
      setQueued(true)
    } catch {
      setFlash([
        {
          type: 'error',
          content: 'Failed to add to queue. Please try again.',
          dismissible: true,
          onDismiss: () => setFlash([]),
        },
      ])
    }
  }

  // Determine whether we are actively generating (runId set or SSE streaming)
  const isGenerating = !!runId || generating || agentStatus === 'running'

  // Compute progress percentage from SSE node statuses
  const doneCount = graphNodes.filter(n => nodeStatuses[n] === 'done').length
  const progressPct = graphNodes.length > 0 ? Math.round((doneCount / graphNodes.length) * 100) : 0

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header={`Generate Email — ${provider.provider_name}`}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Close
            </Button>
            <Button
              variant="primary"
              disabled={!subject || !body || compliance?.overall_severity === 'HIGH' || publishing || queued}
              loading={publishing}
              onClick={handleAddToQueue}
            >
              {queued ? 'Added to Queue' : 'Add to Queue'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {flash.length > 0 && <Flashbar items={flash} />}

        {/* Provider metadata block */}
        <ColumnLayout columns={2} borders="horizontal">
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">NPI</Box>
            <Box>{provider.npi}</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">Email</Box>
            <Box>{email || '—'}</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">CMS Volume</Box>
            <Box>{provider.prescribing_volume_annual_usd ? `$${(provider.prescribing_volume_annual_usd / 1000).toFixed(0)}k` : '—'}</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="awsui-key-label">Fit Score</Box>
            <Badge color={mapBadgeColor(fit.color)}>
              {fit.label} ({fit.total}/10)
            </Badge>
          </SpaceBetween>
        </ColumnLayout>

        {/* Why they fit */}
        <ExpandableSection headerText="Why they fit">
          <SpaceBetween size="xs">
            {fit.talking_points.map((point, i) => (
              <Box key={i} variant="p">
                • {point}
              </Box>
            ))}
          </SpaceBetween>
        </ExpandableSection>

        {/* Generate button + settings — only shown when no subject/body yet and not currently generating */}
        {!subject && !body && !isGenerating && (
          <SpaceBetween size="m">
            <ExpandableSection
              headerText="Generation Settings"
              expanded={settingsExpanded}
              onChange={({ detail }) => setSettingsExpanded(detail.expanded)}
            >
              <SpaceBetween size="m">
                <ColumnLayout columns={2}>
                  <FormField label="Email Style">
                    <Select
                      options={EMAIL_STYLE_OPTIONS}
                      selectedOption={EMAIL_STYLE_OPTIONS.find((o) => o.value === emailStyleOverride) ?? null}
                      onChange={({ detail }) => setEmailStyleOverride((detail.selectedOption.value ?? 'automatic') as EmailStyleOverride)}
                    />
                  </FormField>
                  <FormField label="Framework">
                    <Select
                      options={FRAMEWORK_OPTIONS}
                      selectedOption={FRAMEWORK_OPTIONS.find((o) => o.value === frameworkOverride) ?? null}
                      onChange={({ detail }) => setFrameworkOverride((detail.selectedOption.value ?? 'automatic') as FrameworkOverride)}
                    />
                  </FormField>
                </ColumnLayout>
                <FormField label="Psychology Strategy">
                  <Select
                    options={PSYCHOLOGY_OPTIONS}
                    selectedOption={PSYCHOLOGY_OPTIONS.find((o) => o.value === psychologyStrategy) ?? null}
                    onChange={({ detail }) => setPsychologyStrategy((detail.selectedOption.value ?? 'automatic') as PsychologyStrategy)}
                  />
                </FormField>
              </SpaceBetween>
            </ExpandableSection>
            <Button
              variant="primary"
              onClick={handleGenerate}
            >
              Generate Email
            </Button>
          </SpaceBetween>
        )}

        {/* Generate button loading state — shown while generating */}
        {!subject && !body && isGenerating && (
          <Button
            variant="primary"
            loading={isGenerating}
            disabled={isGenerating}
            onClick={handleGenerate}
          >
            Generate Email
          </Button>
        )}

        {/* Progress — ProgressBar + per-node steps, driven by SSE nodeStatuses */}
        {isGenerating && (
          <SpaceBetween size="m">
            <ProgressBar
              value={progressPct}
              label="Generating email"
              resultText="Done"
              status={progressPct === 100 ? 'success' : 'in-progress'}
            />
            <SpaceBetween size="xs">
              {graphNodes.length === 0 ? (
                <StatusIndicator type="loading">Starting…</StatusIndicator>
              ) : (
                graphNodes.map((nodeName) => {
                  const nodeStatus = nodeStatuses[nodeName]
                  const label = humanizeNodeName(nodeName)
                  let type: 'loading' | 'success' | 'pending' | 'error' = 'pending'
                  if (nodeStatus === 'active') type = 'loading'
                  else if (nodeStatus === 'done') type = 'success'
                  else if (nodeStatus === 'error') type = 'error'
                  return (
                    <StatusIndicator key={nodeName} type={type}>
                      {label}
                    </StatusIndicator>
                  )
                })
              )}
            </SpaceBetween>
          </SpaceBetween>
        )}

        {/* Email editor — shown after generation completes */}
        {subject && body && (
          <SpaceBetween size="m">
            {/* Read-only summary of settings used to generate this email */}
            <ColumnLayout columns={3} borders="horizontal">
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Email Style</Box>
                <Box>{EMAIL_STYLE_OPTIONS.find(o => o.value === emailStyleOverride)?.label ?? emailStyleOverride}</Box>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Framework</Box>
                <Box>{FRAMEWORK_OPTIONS.find(o => o.value === frameworkOverride)?.label ?? frameworkOverride}</Box>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Psychology Strategy</Box>
                <Box>{PSYCHOLOGY_OPTIONS.find(o => o.value === psychologyStrategy)?.label ?? psychologyStrategy}</Box>
              </SpaceBetween>
            </ColumnLayout>
            <FormField label="Subject">
              <Input value={subject} onChange={({ detail }) => setSubject(detail.value)} />
            </FormField>
            <FormField label="Body">
              <Textarea
                value={body}
                onChange={({ detail }) => setBody(detail.value)}
                rows={8}
              />
            </FormField>

            {/* Compliance check */}
            {compliance && (
              <ExpandableSection
                headerText={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box>Compliance Check</Box>
                    <StatusIndicator
                      type={
                        compliance.overall_severity === 'HIGH'
                          ? 'error'
                          : compliance.overall_severity === 'MEDIUM'
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {compliance.overall_severity}
                    </StatusIndicator>
                  </SpaceBetween>
                }
              >
                <SpaceBetween size="xs">
                  {compliance.checks.map((check, i) => (
                    <SpaceBetween key={i} direction="horizontal" size="xs">
                      <StatusIndicator
                        type={
                          check.severity === 'HIGH'
                            ? 'error'
                            : check.severity === 'MEDIUM'
                              ? 'warning'
                              : 'info'
                        }
                      >
                        {check.label}
                      </StatusIndicator>
                      <Box variant="small">{check.detail}</Box>
                    </SpaceBetween>
                  ))}
                  <Button
                    variant="inline-link"
                    onClick={() => checkCompliance(clientId, provider._id, subject, body)}
                  >
                    Re-check Compliance
                  </Button>
                </SpaceBetween>
              </ExpandableSection>
            )}

            {/* Fact Sources */}
            {outreachProvenance.length > 0 && (
              <ExpandableSection
                headerText={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box>Fact Sources</Box>
                    <Box variant="small" color="text-status-inactive">
                      {outreachProvenance.filter(ps => ps.claims.length > 0).length} sentences with citations
                    </Box>
                  </SpaceBetween>
                }
                defaultExpanded={false}
              >
                <SpaceBetween size="m">
                  {outreachProvenance.filter(ps => ps.claims.length > 0).map((ps) => (
                    <SpaceBetween key={ps.sentence} size="xs">
                      <Box variant="awsui-key-label">{ps.sentence}</Box>
                      <SpaceBetween size="xxs">
                        {ps.claims.map((claim) => (
                          <ColumnLayout key={`${claim.field}-${claim.source}`} columns={4} borders="none">
                            <SpaceBetween size="xxs">
                              <Box variant="awsui-key-label">Claim</Box>
                              <Box variant="small">{claim.text}</Box>
                            </SpaceBetween>
                            <SpaceBetween size="xxs">
                              <Box variant="awsui-key-label">Source</Box>
                              <Box variant="small">{claim.source}</Box>
                            </SpaceBetween>
                            <SpaceBetween size="xxs">
                              <Box variant="awsui-key-label">Field</Box>
                              <Box variant="small">{claim.field}: {claim.actualValue}</Box>
                            </SpaceBetween>
                            <SpaceBetween size="xxs">
                              <Box variant="awsui-key-label">Verified</Box>
                              <StatusIndicator type={claim.verified ? 'success' : 'warning'}>
                                {claim.verified ? 'Verified' : 'Unverified'}
                              </StatusIndicator>
                            </SpaceBetween>
                          </ColumnLayout>
                        ))}
                      </SpaceBetween>
                    </SpaceBetween>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  )
}
